import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { User, CreditCard, LogOut, Gift, Clock, Info, Heart, LayoutDashboard, PencilRuler } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";

const Header = () => {
  const { user, profile, signOut } = useAuth();
  const { isSubscribed, creditsRemaining: subscriptionCredits, creditsUsed: subscriptionCreditsUsed, creditsPerPeriod, subscriptionTier } = useSubscription();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleGetStarted = () => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate("/dashboard");
    }
  };

  // Calculate credits based on subscription status
  const profileCredits = Math.max(0, profile?.credits);
  const creditsRemaining = subscriptionCredits + profileCredits;
  const subscriptionCreditsRemaining = isSubscribed ? subscriptionCredits : 0;
  const creditsUsed = isSubscribed ? subscriptionCreditsUsed : 0;
  const totalCredits = isSubscribed ? creditsPerPeriod : 12;
  const isFreeTrial = !isSubscribed;

  const handleUpgradePlan = () => {
    navigate('/pricing');
  };

  const handleQuickLoad = () => {
    navigate('/profile'); // Redirect to profile where quick load is implemented
  };

  return (
    <header className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-50" data-testid="main-header">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Logo 
          onClick={() => navigate(user ? '/dashboard' : '/')}
          data-testid="brand-logo"
        />

        <div className="flex items-center gap-3">
          <ThemeToggle />
          
          {user && profile ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="hidden sm:flex items-center gap-2 px-3 py-1 bg-accent/50 hover:bg-accent rounded-full"
                >
                  <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-md">
                    <CreditCard className="w-3 h-3 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      {creditsRemaining}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">credits remaining</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4 space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{isSubscribed ? subscriptionTier || 'Paid Plan' : 'Free Trial'}</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-primary" />
                        <span className="text-sm">{isSubscribed ? 'Period Credits' : 'Plan Credits'}</span>
                      </div>
                      <span className="font-medium">{totalCredits}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-destructive" />
                        <span className="text-sm">Credits used</span>
                      </div>
                      <span className="font-medium">{creditsUsed}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-sm">Credits remaining</span>
                      </div>
                      <span className="font-medium">{subscriptionCreditsRemaining}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-primary" />
                        <span className="text-sm">Top-up Credits</span>
                      </div>
                      <span className="font-medium">{profileCredits}</span>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t">
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 h-auto text-xs text-muted-foreground"
                      onClick={() => navigate('/help')}
                    >
                      <Info className="w-3 h-3 mr-1" />
                      Credits FAQ
                    </Button>
                  </div>
                  
                  <div className="pt-2">
                    {isFreeTrial ? (
                      <Button 
                        className="w-full" 
                        onClick={handleUpgradePlan}
                      >
                        Upgrade plan
                      </Button>
                    ) : (
                      <Button 
                        className="w-full" 
                        onClick={handleQuickLoad}
                      >
                        Quick Top-Up
                      </Button>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/auth')}
              className="hidden sm:flex items-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Unlock Free Credits
            </Button>
          )}
          
            {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu">
                <User className="w-4 h-4" />
              </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="user-dropdown">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" />
                Dashboard
                </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/generate")} data-testid="nav-generate">
                <PencilRuler className="mr-2 h-4 w-4" />
                Generate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/ecard")} data-testid="nav-ecard">
                <Heart className="mr-2 h-4 w-4" />
                E-Cards
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/gallery")} data-testid="nav-gallery">
                <Gift className="mr-2 h-4 w-4" />
                Gallery
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/themes")} data-testid="nav-themes">
                <Clock className="mr-2 h-4 w-4" />
                Themes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-link">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSignOut} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            ) : (
            <Button variant="ghost" size="icon" onClick={() => navigate('/auth')}>
              <User className="w-4 h-4" />
            </Button>
            )}
          <Button variant="hero" size="sm" onClick={handleGetStarted}>
            {user ? "Let's Create" : "Get Started"}
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;